// @flow
import StorageWrapper from './storage-wrapper';
import {EventManager, Utils, getLogger} from '@playkit-js/playkit-js';

export default class StorageManager {
  static StorageKeys: {[key: string]: string} = {
    MUTED: 'muted',
    VOLUME: 'volume',
    AUDIO_LANG: 'audioLanguage',
    TEXT_LANG: 'textLanguage',
    TEXT_STYLE: 'textStyle'
  };

  static _logger: any = getLogger('StorageManager');

  /**
   * @static
   * @private
   * @returns {boolean} - Whether local storage is implemented in the current browser.
   */
  static isLocalStorageAvailable(): boolean {
    return StorageWrapper.isLocalStorageAvailable();
  }

  /**
   * Attaches the player listeners to the local storage wrapper.
   * @private
   * @param {Player} player - The player reference.
   * @static
   * @returns {void}
   */
  static attach(player: Player): void {
    StorageManager._logger.debug('Attach local storage');
    let eventManager = new EventManager();
    eventManager.listen(player, player.Event.UI.USER_CLICKED_MUTE, () => {
      if (!player.isCasting()) {
        StorageWrapper.setItem(StorageManager.StorageKeys.MUTED, player.muted);
      }
    });
    eventManager.listen(player, player.Event.UI.USER_CLICKED_UNMUTE, () => {
      if (!player.isCasting()) {
        StorageWrapper.setItem(StorageManager.StorageKeys.MUTED, player.muted);
      }
    });

    eventManager.listen(player, player.Event.UI.USER_CHANGED_VOLUME, () => {
      if (!player.isCasting()) {
        if (player.volume > 0) {
          StorageWrapper.setItem(StorageManager.StorageKeys.MUTED, false);
        } else {
          StorageWrapper.setItem(StorageManager.StorageKeys.MUTED, true);
        }
        StorageWrapper.setItem(StorageManager.StorageKeys.VOLUME, player.volume);
      }
    });

    eventManager.listen(player, player.Event.UI.USER_SELECTED_AUDIO_TRACK, event => {
      const audioTrack = event.payload.audioTrack;
      StorageWrapper.setItem(StorageManager.StorageKeys.AUDIO_LANG, audioTrack.language);
    });

    eventManager.listen(player, player.Event.UI.USER_SELECTED_CAPTION_TRACK, event => {
      const textTrack = event.payload.captionTrack;
      StorageWrapper.setItem(StorageManager.StorageKeys.TEXT_LANG, textTrack.language);
    });

    eventManager.listen(player, player.Event.UI.USER_SELECTED_CAPTIONS_STYLE, event => {
      try {
        const textStyle = JSON.stringify(event.payload.captionsStyle);
        StorageWrapper.setItem(StorageManager.StorageKeys.TEXT_STYLE, textStyle);
      } catch (e) {
        StorageManager._logger.error(e.message);
      }
    });

    eventManager.listen(player, player.Event.PLAYER_DESTROY, () => eventManager.destroy());
  }

  /**
   * Gets the player text style from storage.
   * @private
   * @static
   * @returns {?Object} - The stored text style object
   */
  static getPlayerTextStyle(): ?Object {
    return StorageWrapper.getItem(StorageManager.StorageKeys.TEXT_STYLE);
  }

  /**
   * Checks if we have previous storage.
   * @private
   * @static
   * @return {boolean} - Whether we have previous storage.
   */
  static hasStorage(): boolean {
    const storageSize = StorageWrapper.size;
    const hasStorage = storageSize !== 0;
    if (hasStorage) {
      StorageManager._logger.debug('Storage found with size of ', storageSize);
    } else {
      StorageManager._logger.debug('No storage found');
    }
    return hasStorage;
  }

  /**
   * Gets the storage in the structure of the player configuration.
   * @private
   * @static
   * @return {Object} - Partial storageable player configuration.
   */
  static getStorageConfig(): Object {
    const values = StorageManager._getExistingValues();
    const storageConfig = StorageManager._buildStorageConfig(values);
    StorageManager._logger.debug('Gets storage config', storageConfig);
    return storageConfig;
  }

  static _getExistingValues(): Object {
    const obj = {};
    Object.keys(StorageManager.StorageKeys).forEach(key => {
      const value = StorageManager.StorageKeys[key];
      const item = StorageWrapper.getItem(value);
      if (item != null) {
        obj[value] = item;
      }
    });
    return obj;
  }

  static _buildStorageConfig(values: Object): Object {
    const storageConfig = Utils.Object.mergeDeep({}, values);
    delete storageConfig.textStyle;
    return {
      playback: storageConfig
    };
  }
}
